import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  Client,
  ClientContact,
  ClientContactPayload,
  ClientDetail,
  ClientSummary,
  ClientAsset,
  ActivityLog,
  Documentable,
  ExportLink,
  CreateClientRequest,
  EditClientRequest,
  UpdateNotesRequest,
  ClientAssetRequest,
  ClientAssetListQuery,
  ClientListQuery,
  ListQuery,
  Quote,
  Invoice,
  Payment,
  UpdatePaymentStatusRequest,
  Contract,
  Job,
  ClientReport,
  BulkSendClientReportsRequest,
  BulkSendClientReportsResult,
} from '@/types'

// 1:1 with Schema_Specification Section 4.5 (clients).
export const clientsApi = {
  list: (q: ClientListQuery = {}) =>
    apiClient
      .get<ApiList<Client>>('/clients', { params: withListDefaults(q) })
      .then(r => r.data),

  // 2.4.1 - list KPI cards (customers / revenue / to be cashed / contracts).
  summary: () =>
    apiClient
      .get<ApiResponse<ClientSummary>>('/clients/summary')
      .then(r => r.data),

  get: (id: string) =>
    apiClient
      .get<ApiResponse<ClientDetail>>(`/clients/${id}`)
      .then(r => r.data),

  create: (body: CreateClientRequest) =>
    apiClient.post<ApiResponse<Client>>('/clients', body).then(r => r.data),

  update: (id: string, body: EditClientRequest) =>
    apiClient
      .put<ApiResponse<Client>>(`/clients/${id}`, body)
      .then(r => r.data),

  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/clients/${id}`).then(r => r.data),

  // Activate / deactivate a client directly from the list (status toggle).
  activate: (id: string) =>
    apiClient
      .put<ApiResponse<Client>>(`/clients/${id}/activate`)
      .then(r => r.data),

  deactivate: (id: string) =>
    apiClient
      .put<ApiResponse<Client>>(`/clients/${id}/deactivate`)
      .then(r => r.data),

  updateNotes: (id: string, body: UpdateNotesRequest) =>
    apiClient
      .put<ApiResponse<Client>>(`/clients/${id}/notes`, body)
      .then(r => r.data),

  export: (q: ClientListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/clients/export', { params: q })
      .then(r => r.data),

  // --- detail sub-tabs (paginated) ---
  quotes: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Quote>>(`/clients/${id}/quotes`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  contracts: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Contract>>(`/clients/${id}/contracts`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  jobs: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Job>>(`/clients/${id}/jobs`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  reports: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<ClientReport>>(`/clients/${id}/reports`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  exportAllReports: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>(`/clients/${id}/reports/export-all`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  bulkSendReports: (id: string, body: BulkSendClientReportsRequest = {}) =>
    apiClient
      .post<
        ApiResponse<BulkSendClientReportsResult>
      >(`/clients/${id}/reports/bulk-send`, body)
      .then(r => r.data),

  assets: (id: string, q: ClientAssetListQuery = {}) =>
    apiClient
      .get<ApiList<ClientAsset>>(`/clients/${id}/assets`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  createAsset: (id: string, body: ClientAssetRequest) =>
    apiClient
      .post<ApiResponse<ClientAsset>>(`/clients/${id}/assets`, body)
      .then(r => r.data),

  updateAsset: (id: string, assetId: string, body: ClientAssetRequest) =>
    apiClient
      .put<ApiResponse<ClientAsset>>(`/clients/${id}/assets/${assetId}`, body)
      .then(r => r.data),

  deleteAsset: (id: string, assetId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/clients/${id}/assets/${assetId}`)
      .then(r => r.data),

  invoices: (id: string, q: ListQuery & { status?: string } = {}) =>
    apiClient
      .get<ApiList<Invoice>>(`/clients/${id}/invoices`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  payments: (id: string, q: ListQuery & { status?: string } = {}) =>
    apiClient
      .get<ApiList<Payment>>(`/clients/${id}/payments`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // 2.7.12 - effective overdue-blocking mode for the Create-job screen:
  // 'warning' (inline notice), 'hard' (block Save), or null (nothing).
  overdueBlocking: (id: string) =>
    apiClient
      .get<
        ApiResponse<{ mode: 'warning' | 'hard' | null }>
      >(`/clients/${id}/overdue-blocking`)
      .then(r => r.data),

  updatePaymentStatus: (paymentId: string, body: UpdatePaymentStatusRequest) =>
    apiClient
      .put<ApiResponse<Payment>>(`/payments/${paymentId}/status`, body)
      .then(r => r.data),

  documents: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Documentable>>(`/clients/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  uploadDocument: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post<ApiResponse<Documentable>>(`/clients/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Documentable>
      >(`/clients/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),

  deleteDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/clients/${id}/documents/${documentId}`)
      .then(r => r.data),

  createContact: (id: string, body: ClientContactPayload) =>
    apiClient
      .post<ApiResponse<ClientContact>>(`/clients/${id}/contacts`, body)
      .then(r => r.data),

  updateContact: (id: string, contactId: string, body: ClientContactPayload) =>
    apiClient
      .put<
        ApiResponse<ClientContact>
      >(`/clients/${id}/contacts/${contactId}`, body)
      .then(r => r.data),

  deleteContact: (id: string, contactId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/clients/${id}/contacts/${contactId}`)
      .then(r => r.data),

  activity: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<ActivityLog>>(`/clients/${id}/activity`, {
        params: withListDefaults({ limit: 20, ...q }),
      })
      .then(r => r.data),
}

export default clientsApi
